import { useNavigate, useSearch } from "@tanstack/react-router"
import { useCallback, useMemo } from "react"

import { trpc } from "../../lib/trpc"
import type { ICompanyEntry } from "./homeDashboard.types"
import { companyParamFor, selectedCompaniesFor } from "./homeDashboardModel"

const DIRECTORY_STALE_MS = 5 * 60_000
const NO_COMPANIES: ICompanyEntry[] = []
/** Stable default so the memo/callback deps don't change every render */
const NO_FALLBACK: string[] = []

export interface ICompanyFilter {
  directory: ICompanyEntry[]
  /** Selected company slugs; empty = every company */
  selected: string[]
  setSelected: (slugs: string[]) => void
}

/**
 * Home company/product filter. The URL is the source of truth (`?company=vidmax,pebble`) so a filtered home is a
 * link. With no param the active view's default (`fallback`) applies; `?company=all` overrides it with every company.
 */
export const useCompanyFilter = (fallback: string[] = NO_FALLBACK): ICompanyFilter => {
  const navigate = useNavigate()
  const { company } = useSearch({ strict: false }) as { company?: unknown }
  const selected = useMemo(() => selectedCompaniesFor(company, fallback), [company, fallback])
  const directory = trpc.companyHome.getDirectory.useQuery(undefined, {
    staleTime: DIRECTORY_STALE_MS,
    trpc: { context: { skipBatch: true } },
  })

  const setSelected = useCallback(
    (slugs: string[]) =>
      void navigate({
        to: "/",
        search: (prev: Record<string, unknown>) => ({ ...prev, company: companyParamFor(slugs, fallback) }),
      }),
    [navigate, fallback],
  )

  // The API validates with zod; this app's tsconfig widens zod output to optional fields, so narrow it back here
  const companies = useMemo(
    () =>
      directory.data?.filter(
        (c): c is ICompanyEntry => !!c.slug && typeof c.name === "string" && Array.isArray(c.projects),
      ) ?? NO_COMPANIES,
    [directory.data],
  )

  return { directory: companies, selected, setSelected }
}
